import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import Select from 'react-select';
import { useLocation, useNavigate } from 'react-router-dom';
import 'react-quill/dist/quill.snow.css';
import 'bootstrap/dist/css/bootstrap.min.css';
const API_BASE_URL = process.env.REACT_APP_API_URL;
const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image', 'video'],
    ['clean'],
  ],
};

const formats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet',
  'link', 'image', 'video',
];

function MessageForm({ onMessageSaved }) {
  const location = useLocation();
  const navigate = useNavigate();
  const messageToEdit = location.state?.messageToEdit || null;

  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const resData = res.data;
        let rawProducts = [];

        if (Array.isArray(resData)) rawProducts = resData;
        else if (resData.products) rawProducts = resData.products;
        else if (resData.data) rawProducts = resData.data;
        else if (resData.productList) rawProducts = resData.productList;

        const normalized = rawProducts.map((prod) => ({
          value: prod._id?.$oid || prod._id,
          label: prod.name || prod.productName || 'Unnamed Product',
        }));

        setProducts(normalized);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError('Could not load product list.');
      } finally {
        setProductLoading(false);
      }
    };

    fetchProducts();
  }, [token]);

  useEffect(() => {
    if (messageToEdit && products.length > 0) {
      setMessage(messageToEdit.message || '');
      const productIds = (messageToEdit.productIds || []).map(p =>
        typeof p === 'string' ? p : p._id
      );
      const selected = products.filter((p) => productIds.includes(p.value));
      setSelectedProducts(selected);
    }
  }, [messageToEdit, products]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (selectedProducts.length === 0 || !message) {
      setError('Please select at least one product and enter a message.');
      return;
    }

    const productIds = selectedProducts.map((p) => p.value);

    try {
      setLoading(true);

      const payload = { productIds, message };
      const config = { headers: { Authorization: `Bearer ${token}` } };

      let response;

      if (messageToEdit?._id) {
        response = await axios.put(
          `${API_BASE_URL}/api/messages/${messageToEdit._id}`,
          payload,
          config
        );
        setSuccess('Message updated successfully.');
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/messages`,
          payload,
          config
        );
        setSuccess('Message added successfully.');
      }

      onMessageSaved?.(response.data?.data);

      setTimeout(() => {
        navigate('/Message');
      }, 1000);
    } catch (err) {
      console.error('Error saving message:', err);
      const msg = err.response?.data?.message || 'Something went wrong.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/Message');
  };

  return (
    <div className="container mt-4">
      {/* Back button in top-right corner */}
      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-sm"
          onClick={handleCancel}
          style={{
            backgroundColor: '#102234',
            color: 'white',
            border: 'none',
          }}
        >
          ← Back to Messages
        </button>
      </div>

      <div className="card shadow border-0">
        <div className="card-body p-4">
          <h3 className="mb-4" style={{ color: '#102234' }}>
            {messageToEdit ? 'Edit Message' : 'Send a Message'}
          </h3>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-semibold">Select Products</label>
              {productLoading ? (
                <div className="text-muted">Loading products...</div>
              ) : (
                <Select
                  isMulti
                  options={products}
                  value={selectedProducts}
                  onChange={setSelectedProducts}
                  placeholder="Select products send message to...."
                />
              )}
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold">Message Description</label>
              <ReactQuill
                value={message}
                onChange={setMessage}
                modules={modules}
                formats={formats}
                style={{ height: '200px', marginBottom: '40px' }}
              />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                style={{ marginTop: '10px' }}
                className="btn btn-outline-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn"
                style={{ backgroundColor: '#102234', color: 'white', marginTop: '10px' }}
                disabled={loading}
              >
                {loading && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
                {loading
                  ? messageToEdit ? 'Updating...' : 'Submitting...'
                  : messageToEdit ? 'Update Message' : 'Submit Message'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default MessageForm;
